import { SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { fetchAccountInvite, updateAccountInvite } from 'api/account';
import { CampaignSelect } from 'assets/js/pages/account-settings/users/CampaignSelect';
import { UserRoleFields } from 'assets/js/pages/account-settings/users/UserRoleFields';
import { RoleSettings } from 'assets/js/pages/account-settings/users/types';
import {
    campaignSelectionToRequestValue,
    getRoleIdsForSubmission,
} from 'assets/js/pages/account-settings/users/utils';
import { CopyButton } from 'components/copy-button';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useAccountMemberRoles } from 'customHooks/useAccountMemberRoles';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { accountQueryKeys } from 'queries/account-query-keys';
import { AccountMemberRoleId, AccountMemberRoleInfo } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const EditInviteDrawer = () => {
    const { invitesCampaignFiltering } = useFlags();
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const { inviteId } = useParams<{ inviteId: string }>();
    const queryClient = useQueryClient();
    const { goBack } = useDialogRoute();
    const { data: roles } = useAccountMemberRoles();

    const { data: invite } = useSuspenseQuery({
        queryKey: accountQueryKeys.inviteById(accountId, inviteId),
        queryFn: () => fetchAccountInvite(accountId, inviteId),
    });

    const allRoles = roles.data;
    const inviteRoles = invite.roles;

    const methods = Form.useGivebutterForm<RoleSettings>({
        defaultValues: toForm(
            allRoles,
            inviteRoles,
            invite.campaign_ids,
            invite.add_to_future_campaigns,
        ),
    });

    const { reset, handleSubmit, path, watch } = methods;

    const campaigns = watch('campaigns');
    const hasCampaignsSelected = campaigns.length > 0;

    const { mutate: submit } = useMutation({
        mutationFn: (formValues: RoleSettings) =>
            updateAccountInvite(
                accountId,
                inviteId,
                toRequest(formValues, invitesCampaignFiltering),
            ),
        onSuccess: () => {
            toast.success('Changes saved!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.pendingUsers(accountId) });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.membersWithFilters(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.membersWithInvitesWithFilters(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.inviteById(accountId, inviteId),
            });
            reset();
            goBack();
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to edit account member');
        },
    });

    if (invite.accepted_at) {
        return (
            <div className={'contents'}>
                <Drawer.Header>
                    <Drawer.Title>Edit account invite</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body className="flex flex-col gap-6">
                    <div className="text-lg text-red-500">
                        This invite has already been accepted.
                    </div>
                    <div>Find the new account member and edit them instead.</div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel>Cancel</Drawer.Cancel>
                    </div>
                </Drawer.Footer>
            </div>
        );
    }

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<RoleSettings>)}
            >
                <Drawer.Header>
                    <Drawer.Title>Edit account invite</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className={'flex flex-col gap-6'}>
                        <div>
                            <p className="font-medium">
                                {invite.first_name} {invite.last_name}
                            </p>
                            <div className="flex items-center gap-1">
                                <a href={`mailto:${invite.email}`} className="text-sm">
                                    {invite.email}
                                </a>
                                <CopyButton text={invite.email} />
                            </div>
                        </div>
                        <UserRoleFields />
                        {invitesCampaignFiltering && (
                            <>
                                <CampaignSelect />
                                {hasCampaignsSelected && (
                                    <Form.Container>
                                        <div className={'flex items-center gap-2'}>
                                            <Form.Checkbox name={path('addToFutureCampaigns')} />
                                            <Form.Label name={path('addToFutureCampaigns')}>
                                                Allow access to future campaigns
                                            </Form.Label>
                                        </div>
                                        <Form.Error name={path('addToFutureCampaigns')} />
                                    </Form.Container>
                                )}
                            </>
                        )}
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel>Cancel</Drawer.Cancel>
                        <Form.SubmitButton>Save</Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const toForm = (
    roles: AccountMemberRoleInfo[],
    inviteRoles: AccountMemberRoleId[],
    campaigns: number[] | null,
    addToFutureCampaigns: null | boolean,
) => ({
    roles: roles.map(role => ({
        id: role.id,
        name: role.name,
        description: role.description,
        checked: inviteRoles.includes(String(role.id)),
        userCanAssign: role.user_can_assign,
    })),
    campaigns: campaigns ? campaigns.map(String) : [],
    addToFutureCampaigns: addToFutureCampaigns,
});

const toRequest = (form: RoleSettings, invitesCampaignFiltering: boolean) => {
    const campaigns = campaignSelectionToRequestValue(form.campaigns);

    return {
        roles: getRoleIdsForSubmission(form.roles),
        ...(invitesCampaignFiltering
            ? {
                  campaign_ids: campaigns,
                  add_to_future_campaigns:
                      campaigns === null ? undefined : Boolean(form.addToFutureCampaigns),
              }
            : {}),
    };
};

export { EditInviteDrawer };
